import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Home, FileText, Wrench, MessageSquare, DollarSign, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const getStatCards = () => {
    if (user.role === 'agent' || user.role === 'admin') {
      return [
        { title: 'Total Properties', value: stats.total_properties || 0, icon: <Home className="w-6 h-6" />, color: 'blue' },
        { title: 'Active Leases', value: stats.active_leases || 0, icon: <FileText className="w-6 h-6" />, color: 'green' },
        { title: 'Pending Maintenance', value: stats.pending_maintenance || 0, icon: <Wrench className="w-6 h-6" />, color: 'orange' },
        { title: 'Unread Messages', value: stats.unread_messages || 0, icon: <MessageSquare className="w-6 h-6" />, color: 'purple' },
        { title: 'Total Arrears', value: stats.total_arrears || 0, icon: <AlertCircle className="w-6 h-6" />, color: 'red' },
      ];
    } else if (user.role === 'landlord') {
      return [
        { title: 'My Properties', value: stats.my_properties || 0, icon: <Home className="w-6 h-6" />, color: 'blue' },
        { title: 'Total Rent', value: `$${stats.total_rent || 0}`, icon: <DollarSign className="w-6 h-6" />, color: 'green' },
      ];
    } else {
      return [
        { title: 'Rent Amount', value: `$${stats.rent_amount || 0}`, icon: <DollarSign className="w-6 h-6" />, color: 'blue' },
        { title: 'My Maintenance Jobs', value: stats.my_maintenance_jobs || 0, icon: <Wrench className="w-6 h-6" />, color: 'orange' },
      ];
    }
  };

  return (
    <Layout>
      <div data-testid="dashboard" className="max-w-7xl mx-auto animate-fade-in">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name}
          </h1>
          <p className="text-gray-600">Here's what's happening with your properties</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {getStatCards().map((stat, index) => (
              <Card
                key={index}
                data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="card-hover"
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 bg-${stat.color}-100 rounded-lg text-${stat.color}-600`}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* AI Activity Section */}
        {(user.role === 'agent' || user.role === 'admin') && (
          <div className="mt-8">
            <Card data-testid="ai-activity-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  AI Agents Active
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-gray-700">Triage Agent</span>
                    </div>
                    <p className="text-xs text-gray-600">Categorizing messages automatically</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-gray-700">Maintenance Agent</span>
                    </div>
                    <p className="text-xs text-gray-600">Coordinating maintenance jobs</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-gray-700">Arrears Agent</span>
                    </div>
                    <p className="text-xs text-gray-600">Monitoring rent payments</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-gray-700">Lease Agent</span>
                    </div>
                    <p className="text-xs text-gray-600">Tracking lease renewals</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}